import React from 'react';
import { motion } from 'framer-motion';
import type { IncidentsStats } from '../types';

interface IncidentsHeaderProps {
    stats: IncidentsStats;
    onRefresh: () => void;
    isRefreshing: boolean;
    lastUpdate: string | null;
}

export const IncidentsHeader: React.FC<IncidentsHeaderProps> = ({
    stats,
    onRefresh,
    isRefreshing,
    lastUpdate
}) => {
    return (
        <div className="px-[18px] pt-4 pb-2.5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="text-[17px] font-semibold tracking-[-0.3px] text-[var(--text)] mb-1">
                        Сбои и инциденты
                    </h1>
                    <p className="text-[11px] text-[var(--text-muted)]">
                        История всех проблем и уведомлений
                        {lastUpdate && ` · обновлено ${new Date(lastUpdate).toLocaleTimeString('ru-RU')}`}
                    </p>
                </div>

                <motion.button
                    onClick={onRefresh}
                    disabled={isRefreshing}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="
                        flex items-center gap-2 px-3 py-1.5 rounded-lg
                        bg-[var(--bg-elevated)] border border-[var(--border)]
                        text-[var(--text-secondary)] text-[12px] font-medium
                        hover:border-[var(--border-strong)] hover:text-[var(--text)]
                        disabled:opacity-50 disabled:cursor-not-allowed
                        transition-all duration-200
                    "
                >
                    <motion.svg
                        animate={isRefreshing ? { rotate: 360 } : { rotate: 0 }}
                        transition={isRefreshing ? { duration: 1, repeat: Infinity, ease: "linear" } : {}}
                        width="14"
                        height="14"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                    >
                        <polyline points="23 4 23 10 17 10"/>
                        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                    </motion.svg>
                    {isRefreshing ? 'Обновление...' : 'Обновить'}
                </motion.button>
            </div>

            <div className="flex items-center gap-4 mt-4 flex-wrap">
                <StatItem 
                    value={stats.active} 
                    label="Активных" 
                    color="var(--status-down)"
                    pulse={stats.active > 0}
                />
                <StatItem 
                    value={stats.critical} 
                    label="Критических" 
                    color="var(--status-down)"
                />
                <StatItem 
                    value={stats.warning} 
                    label="Предупреждений" 
                    color="var(--status-warn)"
                />
                <div className="w-px h-8 bg-[var(--border)]" />
                <StatItem 
                    value={stats.avgResolutionTime} 
                    label="Среднее время устранения (мин)" 
                    color="var(--text-muted)"
                />
            </div>
        </div>
    );
};

interface StatItemProps {
    value: number;
    label: string;
    color: string;
    pulse?: boolean;
}

const StatItem: React.FC<StatItemProps> = ({ value, label, color, pulse }) => (
    <div className="flex items-center gap-2">
        <span className="relative flex items-center justify-center">
            <span 
                className="text-[20px] font-mono font-semibold"
                style={{ color }}
            >
                {value}
            </span>
            {pulse && (
                <span 
                    className="absolute -right-1 -top-1 w-2 h-2 rounded-full animate-ping"
                    style={{ backgroundColor: color }}
                />
            )}
        </span>
        <span className="text-[11px] text-[var(--text-muted)] leading-tight max-w-[80px]">
            {label}
        </span>
    </div>
);